import React, { useEffect, useState } from 'react';
import API from '../../api';
import EmployeeSidebar from '../smallComponent/EmployeeSidebar';
import RemarkModal from '../smallComponent/RemarkModal';
import * as XLSX from 'xlsx';
import { API_IMAGE_URL } from '../../lib/api';

export default function AdminAttendancePage() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [groupedAttendance, setGroupedAttendance] = useState({});
  const [totalHours, setTotalHours] = useState('0h 0m');
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkData, setRemarkData] = useState({ id: null, remark: '' });
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchEmployees();
    fetchAttendance();
  }, []);

  useEffect(() => {
    fetchAttendance(selectedEmpId);
  }, [selectedEmpId, month, year]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await API.get('/employees', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : res.data.employees || [];
      setEmployees(data);
    } catch (err) {
      console.error('Failed to load employees:', err);
      alert('Failed to load employees');
    }
  };

  const fetchAttendance = async (id = null) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (id) params.append('employee_id', id);
      if (month) params.append('month', month);
      if (year) params.append('year', year);
      const query = params.toString();
      const res = await API.get(`/attendance?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data || !res.data.grouped) {
        setGroupedAttendance({});
        setTotalHours('0h 0m');
        return;
      }
      const monthGroups = {};
      Object.entries(res.data.grouped).forEach(([date, records]) => {
        const d = new Date(date);
        const monthKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
        if (!monthGroups[monthKey]) monthGroups[monthKey] = [];
        monthGroups[monthKey].push({ date, records });
      });
      setGroupedAttendance(monthGroups);
      setTotalHours(res.data.totalHours || '0h 0m');
    } catch (err) {
      console.error('Failed to load attendance:', err);
      alert('Failed to load attendance');
    }
  };

  const handleApprove = async (id, type = 1) => {
    try {
      const token = localStorage.getItem('token');
      await API.post(`/attendance/${id}/approve`, { type }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAttendance(selectedEmpId);
    } catch (err) {
      console.error('Approval failed:', err);
      alert('Approval failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attendance?')) return;
    try {
      const token = localStorage.getItem('token');
      await API.delete(`/attendance/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAttendance(selectedEmpId);
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Delete failed');
    }
  };

  const handleMonthDelete = async () => {
    if (!window.confirm("Delete all this month's attendance?")) return;
    try {
      const token = localStorage.getItem('token');
      await API.post(`/attendance/delete-month`, { employee_id: selectedEmpId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAttendance(selectedEmpId);
    } catch (err) {
      console.error('Month delete failed:', err);
      alert('Month delete failed');
    }
  };

  const handleRemarkUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await API.post(`/attendance/update-remark`, remarkData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowRemarkModal(false);
      fetchAttendance(selectedEmpId);
    } catch (err) {
      console.error('Remark update failed:', err);
      alert('Remark update failed');
    }
  };

  const exportToExcel = () => {
    const employeeName = selectedEmpId ? employees.find(e => e.id === selectedEmpId)?.name || 'Employee' : 'All Employees';
    const excelData = [];
    Object.entries(groupedAttendance).forEach(([monthKey, days]) => {
      days.forEach(({ date, records }) => {
        records.forEach((r, i) => {
          excelData.push({
            SN: i + 1,
            Date: new Date(r.date).toLocaleDateString('en-IN'),
            Employee: r.employee_name,
            Entry: r.entry_time || '-',
            Exit: r.exit_time || '-',
            Hours: r.working_hours || '0',
            Remark: r.remark || '-',
            Approved: r.is_approved === 1 ? 'Full' : r.is_approved === 2 ? 'Half' : 'No',
          });
        });
      });
    });
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');
    XLSX.writeFile(workbook, `Attendance_${employeeName}_${month || 'All'}_${year}.xlsx`);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4">
      <div className="md:col-span-1">
        <EmployeeSidebar
          employees={employees}
          selectedEmpId={selectedEmpId}
          onSelect={setSelectedEmpId}
        />
      </div>

      <div className="md:col-span-3">
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-700">
              Attendance {selectedEmpId && `- ${employees.find(e => e.id === selectedEmpId)?.full_name}`}
            </h2>
            <div className="flex flex-wrap gap-2 items-center">
              <select value={month} onChange={e => setMonth(e.target.value)} className="border px-2 py-1 rounded">
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'short' })}</option>
                ))}
              </select>
              <select value={year} onChange={e => setYear(Number(e.target.value))} className="border px-2 py-1 rounded">
                {Array.from({ length: 5 }, (_, i) => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
              <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                Total Hours: <strong>{totalHours}</strong>
              </span>
              <button onClick={exportToExcel} className="bg-green-600 text-white text-sm px-3 py-1 rounded">📊 Excel</button>
              <button onClick={handleMonthDelete} className="bg-red-600 text-white text-sm px-3 py-1 rounded">Delete Month</button>
            </div>
          </div>

          {Object.keys(groupedAttendance).length === 0 ? (
            <p className="text-gray-500">No attendance records found.</p>
          ) : (
            Object.entries(groupedAttendance).map(([monthKey, days]) => (
              <div key={monthKey} className="mb-6">
                <h3 className="text-lg font-semibold border-b pb-1 mb-3">
                  📅 {new Date(`${monthKey}-01`).toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h3>
                <div className="overflow-x-auto mt-2">
                  <table className="min-w-full text-sm border">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="p-2 border">S.N.</th>
                        <th className="p-2 border">Date</th>
                        <th className="p-2 border">Employee</th>
                        <th className="p-2 border">Entry</th>
                        <th className="p-2 border">Exit</th>
                        <th className="p-2 border">Hours</th>
                        <th className="p-2 border">Remark</th>
                        <th className="p-2 border">Approved</th>
                        <th className="p-2 border">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
  {(() => {
    let sn = 1; // month table ka SN start
    return days.flatMap(({ date, records }) =>
      records.map((r) => (
        // <tr key={r.id}>
        //   <td className="p-2 border">{sn++}</td> {/* increment SN */}
        //   <td className="p-2 border">{new Date(r.date).toLocaleDateString('en-IN')}</td>
        //   <td className="p-2 border">{r.employee_name}</td>
        //   {/* baaki cells */}
        // </tr>
                                  <tr key={r.id} className="border-t">
                            <td className="p-2 border">{sn++}</td>
                            <td className="p-2 border">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                            <td className="p-2 border">{r.employee_name}</td>
                            <td className="p-2 border">
                              {r.entry_photo && <img src={`${API_IMAGE_URL}${r.entry_photo}`} className="w-8 h-10 object-cover" alt="entry" />}
                              <div>{r.entry_time || '-'}</div>
                            </td>
                            <td className="p-2 border">
                              {r.exit_photo && <img src={`${API_IMAGE_URL}${r.exit_photo}`} className="w-8 h-10 object-cover" alt="exit" />}
                              <div>{r.exit_time || '-'}</div>
                            </td>
                            <td className={`p-2 border ${!r.working_hours ? 'bg-red-500 text-white' : (() => {
                              const [h, m] = r.working_hours.split('h').map(s => parseInt(s.trim()) || 0);
                              const totalHrs = h + m / 60;
                              if (totalHrs < 4) return 'bg-red-500 text-white';
                              if (totalHrs < 7) return 'bg-yellow-300';
                              return 'bg-green-500 text-white';
                            })()}`}>{r.working_hours || '0'}</td>
                            <td className="p-2 border">{r.remark || '-'}</td>
                            <td className="p-2 border">
                              {r.attendance_type === 'full_day' && <span className="bg-green-200 px-2 py-1 rounded">Full</span>}
                              {r.attendance_type === 'half_day' && <span className="bg-yellow-200 px-2 py-1 rounded">Half</span>}
                              {r.attendance_type === 'absent' && <span className="bg-red-600 px-2 py-1 rounded">Absent</span>}
                            </td>
                            <td className="p-2 border space-x-1">
                              {!r.is_approved && (
                                <>
                                  <button onClick={() => handleApprove(r.id, 1)} className="bg-green-600 text-white px-2 py-1 rounded text-xs">Full</button>
                                  <button onClick={() => handleApprove(r.id, 2)} className="bg-yellow-600 text-white px-2 py-1 rounded text-xs">Half</button>
                                  <button onClick={() => handleApprove(r.id, 3)} className="bg-red-700 text-white px-2 py-1 rounded text-xs">Absent</button>
                                </>
                              )}
                              <button onClick={() => { setRemarkData({ id: r.id, remark: r.remark }); setShowRemarkModal(true); }} className="bg-blue-600 text-white px-2 py-1 rounded text-xs">Remark</button>
                              <button onClick={() => handleDelete(r.id)} className="bg-red-600 text-white px-2 py-1 rounded text-xs">Delete</button>
                            </td>
                          </tr>
      ))
    );
  })()}
</tbody>

                    {/* <tbody>
                      {days.flatMap(({ date, records }) =>
                        records.map((r, index) => (
                          <tr key={r.id} className="border-t">
                            <td className="p-2 border">{index + 1}</td>
                            <td className="p-2 border">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                            <td className="p-2 border">{r.employee_name}</td>
                            <td className="p-2 border">
                              {r.entry_photo && <img src={`${API_IMAGE_URL}${r.entry_photo}`} className="w-8 h-10 object-cover" alt="entry" />}
                              <div>{r.entry_time || '-'}</div>
                            </td>
                            <td className="p-2 border">
                              {r.exit_photo && <img src={`${API_IMAGE_URL}${r.exit_photo}`} className="w-8 h-10 object-cover" alt="exit" />}
                              <div>{r.exit_time || '-'}</div>
                            </td>
                            <td className={`p-2 border ${!r.working_hours ? 'bg-red-500 text-white' : (() => {
                              const [h, m] = r.working_hours.split('h').map(s => parseInt(s.trim()) || 0);
                              const totalHrs = h + m / 60;
                              if (totalHrs < 4) return 'bg-red-500 text-white';
                              if (totalHrs < 7) return 'bg-yellow-300';
                              return 'bg-green-500 text-white';
                            })()}`}>{r.working_hours || '0'}</td>
                            <td className="p-2 border">{r.remark || '-'}</td>
                            <td className="p-2 border">
                              {r.attendance_type === 'full_day' && <span className="bg-green-200 px-2 py-1 rounded">Full</span>}
                              {r.attendance_type === 'half_day' && <span className="bg-yellow-200 px-2 py-1 rounded">Half</span>}
                              {r.attendance_type === 'absent' && <span className="bg-red-600 px-2 py-1 rounded">Absent</span>}
                            </td>
                            <td className="p-2 border space-x-1">
                              {!r.is_approved && (
                                <>
                                  <button onClick={() => handleApprove(r.id, 1)} className="bg-green-600 text-white px-2 py-1 rounded text-xs">Full</button>
                                  <button onClick={() => handleApprove(r.id, 2)} className="bg-yellow-600 text-white px-2 py-1 rounded text-xs">Half</button>
                                  <button onClick={() => handleApprove(r.id, 3)} className="bg-red-700 text-white px-2 py-1 rounded text-xs">Absent</button>
                                </>
                              )}
                              <button onClick={() => { setRemarkData({ id: r.id, remark: r.remark }); setShowRemarkModal(true); }} className="bg-blue-600 text-white px-2 py-1 rounded text-xs">Remark</button>
                              <button onClick={() => handleDelete(r.id)} className="bg-red-600 text-white px-2 py-1 rounded text-xs">Delete</button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody> */}
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <RemarkModal
        show={showRemarkModal}
        onClose={() => setShowRemarkModal(false)}
        remarkData={remarkData}
        setRemarkData={setRemarkData}
        onSubmit={handleRemarkUpdate}
      />
    </div>
  );
}
